import { Link, Outlet, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/context/authStore';
import { authService } from '@/services/auth';
import InstallAppButton from '@/components/InstallAppButton';
import Logo from '@/components/ui/Logo';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { ModulePermission } from '@/types';
import {
  Home,
  Package,
  UsersRound,
  ShoppingCart,
  Bell,
  LogOut,
  Menu,
  X,
  Tag,
  Key,
  Building2,
  Warehouse,
  Banknote,
  Truck,
  FileText,
  BarChart3,
  Wallet,
  CreditCard,
  TrendingUp,
  Gift,
  Scissors,
  ClipboardList,
  ShieldCheck,
  Star,
  LucideIcon,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import clsx from 'clsx';

// Navegación con permisos requeridos
interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  permission?: ModulePermission; // Si no tiene permission, siempre se muestra
  requiresCombos?: boolean; // Solo visible si el tenant tiene combos habilitados
  requiresStore?: boolean;  // Solo visible si el tenant tiene la tienda online habilitada
  adminOnly?: boolean;      // Solo admin/superadmin (no empleados ni cajeros)
}

// Navegación principal
const allNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home, permission: 'dashboard' },
  { name: 'Productos', href: '/dashboard/products', icon: Package, permission: 'products' },
  { name: 'Combos', href: '/dashboard/combos', icon: Gift, permission: 'products_write', requiresCombos: true },
  { name: 'Fraccionados', href: '/dashboard/fraccionados', icon: Scissors, permission: 'products_write' },
  { name: 'Proveedores', href: '/dashboard/suppliers', icon: Truck, permission: 'products_write' },
  { name: 'Compras', href: '/dashboard/purchase-orders', icon: FileText, permission: 'products_write' },
  { name: 'Inventario', href: '/dashboard/inventory', icon: Warehouse, permission: 'inventory' },
  { name: 'Categorías', href: '/dashboard/categories', icon: Tag, permission: 'products_write' },
  { name: 'Ventas', href: '/dashboard/sales', icon: ShoppingCart, permission: 'sales' },
  { name: 'Clientes VIP', href: '/dashboard/vip', icon: Star, permission: 'sales' },
  { name: 'Pedidos', href: '/dashboard/orders', icon: ClipboardList, permission: 'sales', requiresStore: true },
  { name: 'Caja', href: '/dashboard/cash-register', icon: Banknote, permission: 'cash_register' },
  { name: 'Finanzas', href: '/dashboard/finances', icon: Wallet, permission: 'finances' },
  { name: 'Reportes', href: '/dashboard/reports', icon: BarChart3, permission: 'dashboard' },
  { name: 'Repricing', href: '/dashboard/repricing', icon: TrendingUp, permission: 'products_write' },
  { name: 'Alertas', href: '/dashboard/alerts', icon: Bell, permission: 'alerts' },
];

// Navegación de configuración
const configNavigation: NavItem[] = [
  { name: 'Usuarios', href: '/dashboard/users', icon: UsersRound, permission: 'users' },
  { name: 'Mi Almacén', href: '/dashboard/store-profile', icon: Building2, permission: 'users' },
  { name: 'Auditoría', href: '/dashboard/audit', icon: ShieldCheck, permission: 'users', adminOnly: true },
  { name: 'Facturación', href: '/dashboard/billing', icon: CreditCard, permission: 'users' },
];


export default function Layout() {
  const { user, tenant, logout, impersonation, refreshToken } = useAuthStore();
  const queryClient = useQueryClient();
  // El logo se sirve en /uploads (en prod nginx lo proxea al backend).
  const logoSrc = tenant?.logo_url
    ? `${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || ''}${tenant.logo_url}`
    : undefined;
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Cierre de sesión: revoca los tokens en el servidor y luego limpia el estado local.
  const handleLogout = async () => {
    try {
      await authService.logout(refreshToken);
    } catch {
      // Si falla la revocación (ej. token ya expirado), igual cerramos sesión localmente
    } finally {
      queryClient.clear(); // limpiar caché de datos para no filtrar entre cuentas
      logout();
    }
  };

  // El cambio de contraseña obligatorio ahora se maneja en /change-password
  // Este modal solo se usa para cambio voluntario de contraseña

  // Obtener permisos efectivos del usuario
  const userPermissions = useMemo(() => {
    return user?.effective_permissions || [];
  }, [user?.effective_permissions]);

  // Verificar si el usuario tiene permiso para un módulo
  const hasPermission = (permission?: ModulePermission): boolean => {
    if (!permission) return true; // Sin permiso requerido = siempre visible
    return userPermissions.includes(permission);
  };

  // Solo admin/superadmin (para ítems adminOnly como Auditoría)
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Filtrar navegación principal según permisos (y combos habilitados)
  const filteredNavigation = useMemo(() => {
    return allNavigation.filter(item =>
      hasPermission(item.permission) &&
      (!item.adminOnly || isAdmin) &&
      (!item.requiresCombos || tenant?.combos_enabled !== false) &&
      (!item.requiresStore || tenant?.online_store_enabled === true)
    );
  }, [userPermissions, isAdmin, tenant?.combos_enabled, tenant?.online_store_enabled]);

  // Filtrar navegación de configuración según permisos
  const filteredConfigNavigation = useMemo(() => {
    return configNavigation.filter(item => hasPermission(item.permission) && (!item.adminOnly || isAdmin));
  }, [userPermissions, isAdmin]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Impersonation Banner */}
      <ImpersonationBanner />

      <div className={`flex flex-1 ${impersonation.isImpersonating ? 'pt-12' : ''}`}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <Link to="/dashboard" onClick={() => setSidebarOpen(false)} className="flex items-center gap-2">
              <Logo
                src={logoSrc}
                size="sm"
                showText
                text={tenant?.name || 'ANIMAstore'}
              />
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}

            {/* Navegación de Configuración (si tiene algún permiso de config) */}
            {filteredConfigNavigation.length > 0 && (
              <div className="pt-4 mt-4 border-t">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Configuración
                </p>
                {filteredConfigNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={clsx(
                        'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      )}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Link al Panel de Admin para SUPERADMIN */}
            {user?.role === 'superadmin' && (
              <div className="pt-4 mt-4 border-t">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Administración
                </p>
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors text-indigo-600 hover:bg-indigo-50"
                >
                  <Building2 className="h-5 w-5 mr-3" />
                  Panel de Admin
                </Link>
              </div>
            )}
          </nav>

          {/* Instalar como app (se muestra solo si el dispositivo lo permite) */}
          <div className="px-4 pt-3">
            <InstallAppButton
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-taupe-700 text-cream-50 text-sm font-medium hover:bg-taupe-800 transition-colors"
              label="Instalar app"
            />
          </div>

          {/* User section */}
          <div className="border-t p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-medium">
                    {user?.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.full_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
                title="Cambiar contraseña"
              >
                <Key className="h-5 w-5" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
                title="Cerrar sesión"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de cambio de contraseña (voluntario) */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-auto">
        {/* Top bar (mobile) */}
        <div className="lg:hidden sticky top-0 z-10 bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="p-2 rounded-md hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <Logo
              src={tenant?.logo_url}
              size="sm"
              showText
              text={tenant?.name || 'ANIMAstore'}
            />
            <div className="w-10" /> {/* Spacer */}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
      </div>
    </div>
  );
}
